import commonService from 'utils/commonService';
import apiService from './apiService';
import { BaseRes } from 'bff/models/base/baseRes';
import { Activities, Activity, SysOptionReq } from 'bff/models/info';
import { ReturnCode } from 'constants/enums/returnCode.enum';
import { SysOptType } from 'constants/enums/sysOptType.enum';
import { PaymentMethod } from 'bff/models/product/paymentMethod';
import { SysOptions } from 'bff/models/info/sysoptions';
import { PaymentNccReq, PaymentNccRes } from 'bff/models/product';
import sweetAlertService from 'utils/sweetAlertService';
import { SweetAlertIcon } from 'constants/enums/sweetAlertIcon.enum';

const baseReq = commonService.getApiReqData();
const reqConfig = commonService.getReqConfig();

interface evalReq {
    PET_KIND: string,
    PET_VAR_NO: string,
    PET_VAR_NAME: string,
    PET_STERIL: string,
    PET_BDAY: string,
    START_DATE: string,
    END_DATE: string,
    PET_CHIP: string
}

interface uploadPetImgReq {
  APPLY_NO: string,
  PET_CHIP: string,
  IMAGE_B64: string
}

const fetchPetKinds = async () => {
  const res = await apiService.fetchPetKinds(baseReq, reqConfig);
  if (res.MWHEADER.RETURNCODE !== ReturnCode.CodeSuccess) {
    commonService.showErrorModal(res.MWHEADER.RETURNDESC, res.MWHEADER.RETURNCODE);
  } else {
    return res;
  }
};

const fetchQuestion = async () => {
  const res = await apiService.fetchQuestion(baseReq, reqConfig);
  if (res.MWHEADER.RETURNCODE !== ReturnCode.CodeSuccess) {
    commonService.showErrorModal(res.MWHEADER.RETURNDESC, res.MWHEADER.RETURNCODE);
  } else {
    return res;
  }
};

const evalPet = async (req:evalReq) => {
  const customReq = commonService.getApiReqData(req);
  const res = await apiService.evalPet(customReq, reqConfig);
  if (res.MWHEADER.RETURNCODE !== ReturnCode.CodeSuccess) {
    commonService.showErrorModal(res.MWHEADER.RETURNDESC, res.MWHEADER.RETURNCODE);
  } else {
    return res;
  }
};

const fetchPolicyNo = async (req:any) => {
  const customReq = commonService.getApiReqData(req);
  const res = await apiService.fetchPolicyNo(customReq, reqConfig);
  if (res.MWHEADER.RETURNCODE !== ReturnCode.CodeSuccess) {
    commonService.showErrorModal(res.MWHEADER.RETURNDESC, res.MWHEADER.RETURNCODE);
  } else {
    return res;
  }
};

const uploadPetImg = async (req:uploadPetImgReq) => {
  const customReq = commonService.getApiReqData(req);
  const res = await apiService.uploadPetImg(customReq, reqConfig);
  if (res.MWHEADER.RETURNCODE !== ReturnCode.CodeSuccess) {
    commonService.showErrorModal(res.MWHEADER.RETURNDESC, res.MWHEADER.RETURNCODE);
  } else {
    return res;
  }
};

const fetchOccupation = async () => {
  const res = await apiService.fetchOccupation(baseReq, reqConfig);
  if (res.MWHEADER.RETURNCODE !== ReturnCode.CodeSuccess) {
    commonService.showErrorModal(res.MWHEADER.RETURNDESC, res.MWHEADER.RETURNCODE);
  } else {
    return commonService.filterOccupationOpts(res.TRANRS.OCCUPATION);
  }
};

const validPetInsRule = async (req:any) => {
  const customReq = commonService.getApiReqData(req);
  const res = await apiService.validPetInsRule(customReq, reqConfig);
  if (res.MWHEADER.RETURNCODE !== ReturnCode.CodeSuccess) {
    commonService.showErrorModal(res.MWHEADER.RETURNDESC, res.MWHEADER.RETURNCODE);
  } else {
    return res;
  }
};

const addPetPolicy = async (req:any) => {
  const customReq = commonService.getApiReqData(req);
  const res = await apiService.addPetPolicy(customReq, reqConfig);
  if (res.MWHEADER.RETURNCODE === ReturnCode.CodeSuccess || res.MWHEADER.RETURNCODE === ReturnCode.CodePassApplyNo) {
    return res;
  } else {
    commonService.showErrorModal(res.MWHEADER.RETURNDESC, res.MWHEADER.RETURNCODE);
  }
};

const fetchActivities = async (insTypeId: string) => {
  const baseReq = commonService.getApiReqData();
  const reqConfig = commonService.getReqConfig();

  const result = await Promise.all([
    apiService.fetchActivitiesByInsTypeId(baseReq, reqConfig, insTypeId)
    // apiService.fetchActivities(baseReq, reqConfig)
  ]).then(([activitiesRes]) => {
    const activitiesBaseRes: BaseRes<Activities> = { ...activitiesRes };

    const res = {
      activities: {
        MORE_ACTIVITY_URL: activitiesBaseRes.TRANRS.MORE_ACTIVITY_URL ? activitiesBaseRes.TRANRS.MORE_ACTIVITY_URL : '',
        ACTIVITY: activitiesBaseRes.TRANRS.ACTIVITY ? [...activitiesBaseRes.TRANRS.ACTIVITY.sort((first: Activity, second: Activity) => +first.SHOW_ORDER - +second.SHOW_ORDER)] : new Array(0)
      }
    };

    return res;
  });

  return result;
};

const fetchInitPayment = async (serialNo: string, insTypeId: string) => {
  // console.log('fetchInitPayment serialNo', serialNo);
  const baseReq = commonService.getApiReqData({ APPLY_NO: serialNo, INS_TYPE_ID: insTypeId });
  const reqConfig = commonService.getReqConfig();

  const sysOptionReq: SysOptionReq = {
    PCLASS: [SysOptType.INSROLETYPE]
  };
  // console.log('fetchInitPayment', baseReq);
  const sysOptionBaseReq = commonService.getApiReqData(sysOptionReq);

  const result = await Promise.all([
    apiService.fetchPaymentMethod(baseReq, reqConfig),
    apiService.fetchSysOptions(sysOptionBaseReq, reqConfig)
  ]).then(([paymentMethodRes, sysOptRes]) => {
    const paymentBaseRes: BaseRes<PaymentMethod> = { ...paymentMethodRes };
    const sysOptBaseRes: BaseRes<SysOptions> = { ...sysOptRes };

    const res = {
      paymentMethod: { ...paymentBaseRes.TRANRS },
      insRoleTypeSysOpts: commonService.sortSysOpts(commonService.arrayTypeReturn(sysOptBaseRes.TRANRS[SysOptType.INSROLETYPE]))
    };

    return res;
  });

  return result;
};

const paymentNccc = async (paymentNccReq: PaymentNccReq) => {
  const baseReq = commonService.getApiReqData(paymentNccReq);
  const reqConfig = commonService.getReqConfig();

  const res = await apiService.paymentNccc(baseReq, reqConfig).then((res: BaseRes<PaymentNccRes>) => res);
  if (res.MWHEADER.RETURNCODE !== ReturnCode.CodeSuccess) {
    const htmlStr = res.TRANRS && res.TRANRS.PAYMENT
      ? `${res.TRANRS.PAYMENT.RESPONSECODE} ${res.TRANRS.PAYMENT.RESPONSEMSG}`
      : `${res.MWHEADER.RETURNDESC}<br />(error code : ${res.MWHEADER.RETURNCODE})`;

    sweetAlertService.modal({
      icon: SweetAlertIcon.Info,
      html: htmlStr
    });
  } else {
    return res.MWHEADER.RETURNCODE;
  }
};

const fetchRenewalInfo = async (memberId: any) => {
  const baseReq = commonService.getApiReqData(memberId);
  const reqConfig = commonService.getReqConfig();

  const res = await apiService.fetchRenewal(baseReq, reqConfig);

  if (res.MWHEADER.RETURNCODE === ReturnCode.CodeSuccess) {
    return res.TRANRS.PET_REPOLICY;
  }
};

const fetchChipInfo = async (memberId: any) => {
  const baseReq = commonService.getApiReqData(memberId);
  const reqConfig = commonService.getReqConfig();

  const res = await apiService.fetchChipInfo(baseReq, reqConfig);

  if (res.MWHEADER.RETURNCODE === ReturnCode.CodeSuccess) {
    return res.TRANRS.CHIP_DATAS;
  } else {
    commonService.showErrorModal(res.MWHEADER.RETURNDESC, res.MWHEADER.RETURNCODE);
  }
};

const fetchPetInfo = async (chipData: any) => {
  const baseReq = commonService.getApiReqData(chipData);
  const reqConfig = commonService.getReqConfig();

  const res = await apiService.fetchPetInfo(baseReq, reqConfig);

  if (res.MWHEADER.RETURNCODE === ReturnCode.CodeSuccess) {
    return res.TRANRS.CHIP_DETAIL_DATAS;
  } else {
    commonService.showErrorModal(res.MWHEADER.RETURNDESC, res.MWHEADER.RETURNCODE);
  }
};

export default {
  fetchPetKinds,
  fetchQuestion,
  evalPet,
  fetchPolicyNo,
  uploadPetImg,
  fetchOccupation,
  validPetInsRule,
  addPetPolicy,
  fetchActivities,
  fetchInitPayment,
  paymentNccc,
  fetchRenewalInfo,
  fetchChipInfo,
  fetchPetInfo
};
